export const eventPlannersData = {
  industry: "Event Planners",
  slug: "event-planners",
  
  seo: {
    title: "Intake Forms for Event Planners | HeyIntake",
    description: "Qualify event clients and capture requirements with AI-powered intake forms. Know the budget, date, guest count, and vision before the first consultation.",
    keywords: [
      "event planning intake form",
      "wedding planner questionnaire",
      "corporate event inquiry form",
      "event client onboarding",
      "party planning intake",
      "event planner lead form"
    ]
  },

  heroTitle: "Intake Forms for Event Planners",
  heroSubtitle: "Capture event details, budget, and vision before the first call. Qualify clients, not just inquiries.",
  heroDescription: "Every event inquiry isn't a good fit. HeyIntake helps you collect budget range, guest count, date, venue status, and style preferences so you can identify ideal clients and skip the tire-kickers.",
  
  whyAIScoring: {
    title: "Why Event Planners Need Smart Intake",
    intro: "In event planning, mismatched expectations are expensive. Champagne dreams on a beer budget leads to unhappy clients and negative reviews.",
    paragraphs: [
      "A simple 'I'm planning an event' inquiry tells you nothing. Is it a 20-person dinner party or a 500-person corporate gala? Do they have $5,000 or $50,000? Are they planning for next month or next year? Without this context, you're flying blind into consultations.",
      "HeyIntake captures the essential details before you spend an hour on a discovery call. Event type, guest count, date, venue status, budget range, and their vision for the event. Our AI identifies which inquiries match your ideal client profile and flags potential red flags.",
      "For wedding planners, corporate event coordinators, and party planners, this means consultations with qualified clients, realistic expectations from day one, and a portfolio of events that match your brand."
    ]
  },

  painPoints: [
    {
      title: "Budget Mismatch",
      description: "Spending an hour on a consultation only to discover their budget is a fraction of what the event requires."
    },
    {
      title: "Unrealistic Timelines",
      description: "Inquiries for elaborate events 3 weeks out that would normally require 6 months of planning."
    },
    {
      title: "Vague Requirements",
      description: "'I want something elegant' could mean anything — you need specifics to quote accurately."
    },
    {
      title: "Date Flexibility Unknown",
      description: "Checking vendor availability for a date they're not even committed to."
    },
    {
      title: "Scope Creep Risk",
      description: "No documented requirements means constant 'can you also...' additions."
    },
    {
      title: "Ghost Inquiries",
      description: "Leads who inquire, get proposals, and disappear without responding."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Basics",
      fields: [
        "Your name",
        "Phone and email",
        "How did you hear about us?",
        "Are you the decision-maker?",
        "Best time to reach you"
      ]
    },
    {
      category: "Event Details",
      fields: [
        "Type of event (wedding, corporate, birthday, etc.)",
        "Event date (firm or flexible)",
        "Guest count (estimated)",
        "Event location/venue (booked or searching)",
        "Indoor, outdoor, or both"
      ]
    },
    {
      category: "Vision & Style",
      fields: [
        "Event theme or style",
        "Color palette preferences",
        "Must-have elements",
        "Pinterest board or inspiration links",
        "Any vendors already booked?"
      ]
    },
    {
      category: "Budget & Services",
      fields: [
        "Total event budget range",
        "What planning services do you need?",
        "Full planning, partial, or day-of coordination",
        "Previous event planning experience",
        "Specific concerns or challenges"
      ]
    }
  ],

  exampleIntakeForm: {
    title: "Example Event Planning Intake Form",
    description: "Here's what a qualifying event intake looks like:",
    fields: [
      { label: "What type of event are you planning?", type: "select", options: ["Wedding", "Corporate Event", "Birthday/Anniversary", "Fundraiser/Gala", "Holiday Party", "Other"] },
      { label: "Event date", type: "text", placeholder: "MM/DD/YYYY or Flexible" },
      { label: "Is this date firm or flexible?", type: "select", options: ["Firm - already booked venue", "Somewhat flexible", "Very flexible"] },
      { label: "Estimated guest count", type: "select", options: ["Under 50", "50-100", "100-200", "200-500", "500+"] },
      { label: "Total event budget", type: "select", options: ["$5K-$15K", "$15K-$30K", "$30K-$50K", "$50K-$100K", "$100K+"] },
      { label: "What planning help do you need?", type: "select", options: ["Full planning", "Partial planning", "Day-of coordination", "Consulting only"] },
      { label: "Describe your vision for the event", type: "textarea" }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Event Planners",
    subtitle: "From inquiry to qualified consultation in three steps",
    steps: [
      {
        title: "Client Shares Their Vision",
        description: "Potential clients provide event details, date, budget, and style preferences through your branded form."
      },
      {
        title: "AI Qualifies & Matches",
        description: "Our AI scores budget fit, timeline feasibility, and alignment with your ideal client profile."
      },
      {
        title: "Consult with Confidence",
        description: "Enter consultations knowing the scope, budget, and vision. Close more aligned clients."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Client Qualification for Planners",
    description: "HeyIntake's AI helps you identify dream clients and politely decline budget mismatches before wasting time.",
    factors: [
      "Budget alignment with event scope",
      "Timeline feasibility",
      "Decision-maker involvement",
      "Vision clarity and realistic expectations",
      "Fit with your ideal event type"
    ],
    exampleLead: {
      name: "Rachel & Tom",
      context: "Wedding • 150 guests • $75K Budget • October 2027",
      summary: "Planning fall wedding, 150 guests, venue booked at local vineyard. $75K total budget, seeking full planning. Clear vision, realistic timeline. Ideal client match.",
      score: 95
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Qualify Budget Fit Instantly",
      description: "Know if their budget matches their vision before the first call."
    },
    {
      icon: "clock",
      title: "Flag Unrealistic Timelines",
      description: "AI alerts you when requested dates don't allow adequate planning time."
    },
    {
      icon: "file",
      title: "Capture Vision Details",
      description: "Collect Pinterest boards, color palettes, and must-haves before consultation."
    },
    {
      icon: "users",
      title: "Identify Decision Makers",
      description: "Know who's involved in the planning decisions before investing time."
    },
    {
      icon: "zap",
      title: "Automate Follow-Up",
      description: "Send proposals, contracts, and timelines automatically based on intake."
    },
    {
      icon: "chart",
      title: "Track Event Pipeline",
      description: "See all inquiries from initial contact through contracted event."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Photographers", path: "/intake-form-for-photographers" },
    { name: "Intake Forms for Agencies", path: "/intake-form-for-agencies" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" },
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" }
  ],

  ctaText: "Ready to Book Better Events?"
};
